// src/pages/NSKabelBlock.jsx
// NS-Kabelverbindung Trafo zur NSHV - pro Trafo erfasst.
// Aussenleiter L1-L3 und N-Leiter als zwei JSONB-Felder.

import { C, Field, TextInput, Select, Row, Subsection } from './formui';
import { t } from '../lib/sprache';

// Auswahllisten: gespeichert bleibt value, nur die Anzeige wird uebersetzt.
const mitT = (liste) => liste.map(o => ({ ...o, label: t(o.label) }));
const BAUFORM = [
  { value: 'einzelader', label: 'Einzelader' },
  { value: 'starkstrom', label: 'Starkstromkabel' },
  { value: '4-adrig',    label: '4-adrig' },
  { value: '5-adrig',    label: '5-adrig' },
  // mit Textfeld (Anna-Lena 18.09.2026)
  { value: 'sonstiges',  label: 'sonstiges' },
];
const QUERSCHNITTE = ['50', '70', '95', '120', '150', '185', '240', '300', '400', '500'];
const KABELTYPEN = [
  { value: 'NYY-J/O', label: 'NYY-J/O' },
  { value: 'NYCWY',   label: 'NYCWY' },
  { value: 'NYY',     label: 'NYY' },
  { value: 'NAYY',    label: 'NAYY' },
  { value: 'NAYY-J/O',label: 'NAYY-J/O' },
  { value: 'NSGAFOEU',label: 'NSGAFÖU' },
  { value: 'sonstiges', label: 'sonstiges' },
];
// Kabeltyp haengt an der Bauform (Anna-Lena 18.09.2026). 4- und 5-adrig gleich.
const KABELTYP_JE_BAUFORM = {
  einzelader: ['NYY-J/O', 'NAYY-J/O', 'NSGAFOEU', 'sonstiges'],
  starkstrom: ['NYCWY', 'NYY', 'NAYY', 'sonstiges'],
  '4-adrig':  ['NYY', 'NAYY', 'NYCWY'],
  '5-adrig':  ['NYY', 'NAYY', 'NYCWY'],
  // Bauform "sonstiges": alle Kabeltypen
  sonstiges: null,
};
const ADERN = Array.from({ length: 10 }, (_, k) => ({ value: String(k + 1), label: String(k + 1) }));
// "Anschluss am Transformator" und die Kabelschuh-Loecher sind seit 18.09.2026
// raus (Anna-Lena: doppelt zum US-Anschluss). Gespeicherte Werte bleiben im Datensatz.

// Hilfs-Komponente: Buttons-Reihe (statt Select)
function ButtonRow({ value, onChange, options }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {options.map(o => {
        const v = typeof o === 'string' ? o : o.value;
        const lab = typeof o === 'string' ? o : o.label;
        const sel = value === v;
        return (
          <button key={v} type='button' onClick={() => onChange(sel ? null : v)}
            style={{ minHeight: 44, padding: '8px 14px', borderRadius: 8, fontSize: 15,
              background: sel ? C.navy : C.white, color: sel ? C.white : C.gray700,
              border: '1.5px solid ' + (sel ? C.navy : C.gray200),
              cursor: 'pointer', fontWeight: sel ? 800 : 600 }}>
            {lab}
          </button>
        );
      })}
    </div>
  );
}

// Frueher Mehrfachauswahl (Array), jetzt EINE Bauform - alte Werte lesen.
const bauformVon = (b) => (Array.isArray(b) ? b[0] : b) || null;

// Nur die passenden Knoepfe; ein gespeicherter, nicht mehr passender Wert
// bleibt sichtbar - nichts verschwindet stillschweigend.
function gefiltert(liste, erlaubt, aktuell) {
  if (!erlaubt) return liste;
  return liste.filter(o => erlaubt.includes(o.value) || o.value === aktuell);
}

// Ein Leiter-Block: Aussenleiter L1-L3 oder N-Leiter.
function LeiterBlock({ titel, daten, setzen, adernFelder }) {
  const bauform = bauformVon(daten.bauform);
  const weitereBauformen = (Array.isArray(daten.bauform) ? daten.bauform.slice(1) : [])
    .map(v => BAUFORM.find(o => o.value === v)?.label || v);
  const typen = gefiltert(KABELTYPEN, bauform ? KABELTYP_JE_BAUFORM[bauform] : null, daten.kabeltyp);
  const bauformWaehlen = (v) => {
    const patch = { bauform: v };
    if (v !== 'sonstiges') patch.bauform_text = null;
    // Passt der Kabeltyp nicht mehr zur neuen Bauform, wird er geleert.
    if (v && daten.kabeltyp && KABELTYP_JE_BAUFORM[v] && !KABELTYP_JE_BAUFORM[v].includes(daten.kabeltyp)) { patch.kabeltyp = null; patch.kabeltyp_text = null; }
    setzen(patch);
  };
  return (
    <div style={{ background: C.gray100, borderRadius: 8, padding: 12, marginBottom: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
        {titel}
      </div>

      <Field label={t('Querschnitt [mm²]')}>
        <ButtonRow value={daten.querschnitt} onChange={v => setzen({ querschnitt: v })} options={QUERSCHNITTE}/>
      </Field>

      <Field label={t('Bauform')} hint={weitereBauformen.length > 0
        ? t('Früher waren mehrere Bauformen gespeichert: {liste}. Beim Auswählen bleibt nur eine.', { liste: weitereBauformen.join(', ') })
        : undefined}>
        <ButtonRow value={bauform} onChange={bauformWaehlen} options={mitT(BAUFORM)}/>
      </Field>
      {bauform === 'sonstiges' && (
        <Field label={t('Bauform (sonstiges)')}>
          <TextInput value={daten.bauform_text || ''} onChange={v => setzen({ bauform_text: v })} placeholder={t('Bauform eintragen')}/>
        </Field>
      )}

      <Field label={t('Kabeltyp')} hint={bauform ? undefined : t('Erst die Bauform wählen, dann passt die Liste')}>
        <ButtonRow value={daten.kabeltyp} onChange={v => setzen(v === 'sonstiges' ? { kabeltyp: v } : { kabeltyp: v, kabeltyp_text: null })} options={mitT(typen)}/>
      </Field>
      {daten.kabeltyp === 'sonstiges' && (
        <Field label={t('Kabeltyp (sonstiges)')}>
          <TextInput value={daten.kabeltyp_text || ''} onChange={v => setzen({ kabeltyp_text: v })} placeholder={t('Kabeltyp eintragen')}/>
        </Field>
      )}

      <Row columns={adernFelder.length}>
        {adernFelder.map(([feld, label]) => (
          <Field key={feld} label={t(label)}>
            <Select value={daten[feld] != null ? String(daten[feld]) : ''} onChange={v => setzen({ [feld]: v ? Number(v) : null })}
              options={daten[feld] != null && !ADERN.some(o => o.value === String(daten[feld]))
                ? [{ value: String(daten[feld]), label: String(daten[feld]) }, ...ADERN] : ADERN}/>
          </Field>
        ))}
      </Row>

    </div>
  );
}

export default function NSKabelBlock({ trafo, onChange }) {
  const al = trafo.ns_kabel_aussenleiter || {};
  const nl = trafo.ns_kabel_neutral || {};

  // Reihenfolge der Felder (Anna-Lena 18.09.2026): Querschnitt, Bauform,
  // Kabeltyp passend zur Bauform. N-Leiter: kein "PE + N" mehr.
  return (
    <Subsection title={t('NS-Kabelverbindung Trafo zur NSHV')}>
      <LeiterBlock titel={t('Außenleiter L1 • L2 • L3')} daten={al}
        setzen={(patch) => onChange('ns_kabel_aussenleiter', { ...al, ...patch })}
        adernFelder={[['adern_l1', 'Adernanzahl L1'], ['adern_l2', 'Adernanzahl L2'], ['adern_l3', 'Adernanzahl L3']]}/>
      <LeiterBlock titel={t('N-Leiter')} daten={nl}
        setzen={(patch) => onChange('ns_kabel_neutral', { ...nl, ...patch, ...(nl.leiter_art ? {} : { leiter_art: 'n_leiter' }) })}
        adernFelder={[['adern_n', 'Adernanzahl N']]}/>
    </Subsection>
  );
}
